/**
 * DataTable - Composant pour afficher des données tabulaires
 * 
 * Table simple et responsive pour les widgets de type table/documents.
 */

import React, { useMemo, useState } from "react";
import { Typography } from "@/components/ui";
import useTranslation from "@/hooks/use-translation";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import type { TableData, TableVisualConfig } from "@/models/widget-v2";

// ============================================================================
// Types
// ============================================================================

interface DataTableProps {
  data: TableData;
  visual: TableVisualConfig;
  maxHeight?: string;
}

type SortDirection = "asc" | "desc" | null;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Formate une valeur de cellule pour l'affichage
 */
function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "number") {
    return new Intl.NumberFormat("fr-FR").format(value);
  }
  if (value instanceof Date) {
    return new Intl.DateTimeFormat("fr-FR").format(value);
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Trie les données selon une colonne
 */
function sortData(
  rows: Array<Record<string, unknown>>,
  sortKey: string | null,
  sortDirection: SortDirection
): Array<Record<string, unknown>> {
  if (!sortKey || !sortDirection) return rows;
  
  return [...rows].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    
    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    let comparison = 0;
    if (typeof aVal === "number" && typeof bVal === "number") {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }
    
    return sortDirection === "desc" ? -comparison : comparison;
  });
}

// ============================================================================
// Sub-components
// ============================================================================

interface SortIconProps {
  direction: SortDirection;
}

const SortIcon: React.FC<SortIconProps> = ({ direction }) => {
  if (direction === "asc") return <FaSortUp size={12} />;
  if (direction === "desc") return <FaSortDown size={12} />;
  return <FaSort size={12} className="opacity-30" />;
};

// ============================================================================
// Main Component
// ============================================================================

export const DataTable: React.FC<DataTableProps> = ({
  data,
  visual,
  maxHeight = "400px",
}) => {
  const { t } = useTranslation();
  const { columns, rows } = data;
  const { title, subtitle } = visual;
  
  // Extraire les options visuelles avec valeurs par défaut
  const striped = visual.striped ?? true;
  const compact = visual.compact ?? false;
  const showRowNumbers = visual.showRowNumbers ?? false;
  
  // État du tri
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Données triées
  const sortedRows = useMemo(
    () => sortData(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection]
  );
  
  // Gestion du clic sur un header
  const handleHeaderClick = (key: string) => {
    if (sortKey === key) {
      // Cycle: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortKey(null);
        setSortDirection(null);
      }
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };
  
  // Cas vide
  if (columns.length === 0 || rows.length === 0) {
    return (
      <div className="h-full w-full flex flex-col">
        {(title || subtitle) && (
          <div className="px-4 pt-4 pb-2">
            {title && (
              <Typography variant="h6" className="text-content-primary">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="text-sm" className="text-content-tertiary">
                {subtitle}
              </Typography>
            )}
          </div>
        )}
        <div className="flex-1 flex items-center justify-center text-content-tertiary">
          <Typography variant="text-sm">Aucune donnée à afficher</Typography>
        </div>
      </div>
    );
  }
  
  // Classes pour le padding des cellules
  const cellPadding = compact ? "px-3 py-2" : "px-4 py-3";
  const headerPadding = compact ? "px-3 py-2" : "px-4 py-3";
  
  return (
    <div className="h-full w-full flex flex-col">
      {/* Titre et sous-titre */}
      {(title || subtitle) && (
        <div className="px-4 pt-4 pb-2">
          {title && (
            <Typography variant="h6" className="text-content-primary">
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="text-sm" className="text-content-tertiary">
              {subtitle}
            </Typography>
          )}
        </div>
      )}
      
      {/* Table */}
      <div 
        className="flex-1 overflow-auto" 
        style={{ maxHeight: title || subtitle ? `calc(${maxHeight} - 60px)` : maxHeight }}
      >
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-arc-surface-secondary z-10">
            <tr>
              {/* Colonne numéro de ligne */}
              {showRowNumbers && (
                <th
                  className={`${headerPadding} text-center text-xs font-semibold text-content-tertiary uppercase tracking-wider border-b border-arc-border w-12`}
                >
                  #
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${headerPadding} text-left text-xs font-semibold text-content-secondary uppercase tracking-wider cursor-pointer hover:bg-arc-surface-tertiary transition-colors border-b border-arc-border`}
                  onClick={() => handleHeaderClick(column.key)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    <SortIcon direction={sortKey === column.key ? sortDirection : null} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-arc-border">
            {sortedRows.map((row, rowIndex) => (
              <tr 
                key={rowIndex} 
                className={`
                  hover:bg-arc-surface-secondary transition-colors
                  ${striped && rowIndex % 2 === 1 ? "bg-arc-surface-secondary/50" : ""}
                `}
              >
                {/* Numéro de ligne */}
                {showRowNumbers && (
                  <td
                    className={`${cellPadding} text-center text-xs text-content-tertiary`}
                  >
                    {rowIndex + 1}
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`${cellPadding} text-sm text-content-primary whitespace-nowrap`}
                  >
                    {formatCellValue(row[column.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Footer avec le nombre de lignes */}
      <div className={`${compact ? "px-3 py-1" : "px-4 py-2"} border-t border-arc-border bg-arc-surface-secondary`}>
        <Typography variant="text-xs" className="text-content-tertiary">
          {t("n_rows", { count: rows.length })}
        </Typography>
      </div>
    </div>
  );
};

export default DataTable;
